import { LoadingButton } from "@mui/lab";
import { Box, Typography } from "@mui/material";
import Address from "@repo/legacy/components/shared/additionalInformation/Address";
import SectionHeader from "@repo/legacy/components/shared/SectionHeader";
import { AddressInformation, type Contact } from "@repo/server-api";
import { useBoundDeep } from "@repo/shared/common";
import { BoundDateField, BoundTextField } from "@repo/shared/components";
import { useTranslation } from "next-i18next";

import { ContactTermsOfPayment, PersonIdentification } from "@repo/shared-contact/components";
import { useUpdateAccountHook } from "@repo/shared-contact/hooks";

import type { SetStateAction } from "react";
import type { Account } from "src/types/ContactTypes";

export type AccountProps = {
	account: Account;
	setAccount: (value: SetStateAction<Account>) => void;
};

export const ContactAccount = (props: AccountProps) => {
	const { t } = useTranslation();

	const { account, setAccount } = props;

	const { updateAccount, loading } = useUpdateAccountHook({
		onSaved: (updatedAccount) => {
			setAccount(updatedAccount);
		},
	});

	const useBinding = <TValue,>(selector: (m: Account) => TValue) => {
		return useBoundDeep<Account, TValue>(account, setAccount, selector);
	};

	const handleSubmit = async () => {
		updateAccount(account);
	};

	const emailField = useBinding((m) => m.email);
	const phoneField = useBinding((m) => m.phone);
	const mobileField = useBinding((m) => m.mobile);
	const birthdayField = useBinding((m) => m.birthday);

	return (
		<Box
			style={{
				minHeight: "90vh",
				width: "100%",
				display: "flex",
				flexDirection: "column",
			}}>
			<Box sx={{ px: 2.5, flex: 1 }}>
				<Box sx={{ mb: 3 }}>
					<Typography variant="h5" color="primary" component="div">
						{account.name}
						<Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
							({account.accountType?.map((x) => t(x.toLowerCase())).join(", ")})
						</Typography>
					</Typography>
				</Box>
				<SectionHeader title={t("profile")} isFirst />
				<PersonIdentification
					isDialog={true}
					contact={account as unknown as Contact}
					readOnly={true}
					setContact={setAccount as unknown as (value: SetStateAction<Contact>) => void}
				/>
				<Box className="fullCenter" sx={{ mt: 2, mb: 2 }}>
					<BoundDateField
						boundedValue={birthdayField}
						label={t("birthday")}
						additionalProps={{ className: "left w-100" }}
					/>
				</Box>
				<Box className="fullCenter" sx={{ mb: 2 }}>
					<BoundTextField
						boundedValue={emailField}
						label={t("email")}
						additionalProps={{ className: "left w-100", inputProps: { readOnly: true } }}
					/>
				</Box>
				<Box className="fullCenter" sx={{ mb: 2 }}>
					<BoundTextField
						boundedValue={phoneField}
						label={t("phone")}
						additionalProps={{ className: "left w-100" }}
					/>
				</Box>
				<Box className="fullCenter" sx={{ mb: 2 }}>
					<BoundTextField
						boundedValue={mobileField}
						label={t("mobile")}
						additionalProps={{ className: "left w-100" }}
					/>
				</Box>
				<SectionHeader title={t("address")} />
				<Address isLoaded address={account.address as AddressInformation} readOnly />
				<ContactTermsOfPayment contact={account} setContact={setAccount} />
			</Box>
			<Box sx={{ display: "flex", justifyContent: "flex-end", py: 2, pr: "30px" }}>
				<LoadingButton
					loading={loading}
					className={loading ? "" : "blueButton"}
					color="secondary"
					variant="contained"
					onClick={handleSubmit}>
					{t("save")}
				</LoadingButton>
			</Box>
		</Box>
	);
};
